import { FC, ReactNode } from "react";
import { styled } from "@mui/material";
import { TreeItem, TreeItemProps } from "@mui/x-tree-view/TreeItem";
import { tokens } from "@/components/Application/theme";

// --- helpers ---
let __autoId = 0;
const extractText = (n: ReactNode): string =>
  typeof n === "string" || typeof n === "number"
    ? String(n)
    : Array.isArray(n)
    ? n.map(extractText).join(" ").trim()
    : "";

const toSafeId = (id: unknown, label: ReactNode): string => {
  const s = (id ?? "").toString().trim();
  if (s) return s;

  // try label-derived fallback (good for folders that lack ids)
  const fromLabel = extractText(label);
  if (fromLabel) return `auto:${fromLabel}`;

  // final fallback: unique counter (dev-only warning)
  if (import.meta?.env?.MODE !== "production") {
    // eslint-disable-next-line no-console
    console.warn(
      "[CustomTreeItem] Missing itemId and label text; generating a temporary id."
    );
  }
  __autoId += 1;
  return `auto:${__autoId}`;
};

// --- styles ---
const StyledTreeItem = styled(TreeItem)<TreeItemProps>(({ theme }) => {
  const colors = tokens(theme.palette.mode);
  return {
    "&:nth-of-type(odd)": {
      borderRadius: "10px",
      backgroundColor: colors.grey[800],
    },
    "& .MuiTreeItem-content .MuiTreeItem-label": {
      fontSize: "14px",
      display: "flex",
      justifyContent: "space-between",
    },
  };
});

// --- component ---
interface CustomTreeItemProps {
  children?: ReactNode | ReactNode[];
  itemId: string | number | null | undefined; // allow nullish, we'll normalize
  label: ReactNode | ReactNode[];
  disabled?: boolean;
}

export const CustomTreeItem: FC<CustomTreeItemProps> = ({
  children,
  itemId,
  label,
  disabled = false,
}) => {
  const safeId = toSafeId(itemId, label);
  return (
    <StyledTreeItem label={label} itemId={safeId} disabled={disabled}>
      {children}
    </StyledTreeItem>
  );
};
